// CallVorgangButton.tsx — der Knopf „Vorgang ▾" mit seinen zwei Wegen.
//
// Vorher stand an beiden Stellen (Anrufzeile und Anruf-Detail) ein Knopf
// „Neuer Vorgang +“, der sofort den Erstell-Flow öffnete. Einen Anruf an einen
// SCHON BESTEHENDEN Vorgang zu hängen war von hier aus gar nicht möglich —
// obwohl das der häufigere Fall ist, sobald zu einer Sache schon ein Vorgang
// läuft. Der Knopf öffnet deshalb jetzt ein Menü mit beiden Wegen.
//
// Der Menü-Körper ist `RdContextMenu` (Portal an document.body, damit die
// Listen-Scrollbox ihn nicht abschneidet). Der Knopf gibt sich ihm als Anker zu
// erkennen — sonst wäre er nicht zuklappbar, siehe `ankerRef` dort.

import React, { useCallback, useRef, useState } from "react";
import { TaskListLtr20Regular } from "@fluentui/react-icons";
import {
    RdContextMenu,
    type RdContextMenuPosition,
    type RdMenuItem,
} from "../redesign/table/RdContextMenu";
import { I } from "../redesign/kvpIcons";
import { menuAnkerUnter, vorgangKnopfTexte } from "./callVorgangZuweisen";

export interface CallVorgangButtonProps {
    /**
     * Wo der Knopf steht. Das entscheidet nur über die Klassen:
     * `zeile` = kompakter Zeilen-Knopf (Label fällt auf schmalen Schirmen weg),
     * `detail` = breiter Knopf der Detail-Aktionsleiste.
     */
    variante: "zeile" | "detail";
    /** Menüpunkt 1 — neuen Vorgang anlegen (bisheriges Verhalten des Knopfes). */
    onNeu: () => void;
    /** Menüpunkt 2 — an einen bestehenden Vorgang hängen. */
    onZuweisen: () => void;
    /**
     * An wie vielen Vorgängen/To-dos hängt der Anruf (PO 01.10.2026: ein Anruf
     * kann an mehreren hängen)? Ab 1 trägt der Knopf Haken und Zahl, und die
     * Menüpunkte sagen, dass sie HINZUFÜGEN.
     */
    anzahl?: number;
    /**
     * Gesetzt = Knopf ist gesperrt; der Text erklärt, warum (Tooltip). T-192:
     * Mailbox-Nachricht, deren Anruf noch nicht synchronisiert ist.
     */
    gesperrtHinweis?: string | null;
}

const CallVorgangButton: React.FC<CallVorgangButtonProps> = ({
    variante,
    onNeu,
    onZuweisen,
    anzahl = 0,
    gesperrtHinweis = null,
}) => {
    const zugeordnet = anzahl > 0;
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const [menu, setMenu] = useState<RdContextMenuPosition | null>(null);

    const toggle = useCallback((e: React.MouseEvent) => {
        // In der Liste liegt der Knopf IN einer klickbaren Zeile — ohne das hier
        // würde zusätzlich der Anruf ausgewählt.
        e.stopPropagation();
        setMenu((offen) => (offen ? null : menuAnkerUnter(btnRef.current)));
    }, []);

    const schliessen = useCallback(() => setMenu(null), []);

    const texte = vorgangKnopfTexte(anzahl);
    const items: RdMenuItem[] = [
        { id: "neu", label: texte.neu, icon: "+", onSelect: onNeu },
        { id: "zuweisen", label: texte.zuweisen, icon: "→", onSelect: onZuweisen },
    ];

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                className={
                    (variante === "zeile" ? "tp-rowact tp-rowvorgang" : "tp-detail-btn") +
                    (zugeordnet ? " tp-vg-zugeordnet" : "")
                }
                onClick={gesperrtHinweis ? (e) => e.stopPropagation() : toggle}
                // aria-disabled statt disabled: Ein echt gesperrter Knopf zeigt
                // in manchen Browsern seinen Tooltip nicht — und der erklärt hier,
                // warum es (noch) nicht geht.
                aria-disabled={gesperrtHinweis ? true : undefined}
                aria-label={gesperrtHinweis ? `${texte.titel} – ${gesperrtHinweis}` : texte.titel}
                aria-haspopup="menu"
                aria-expanded={menu != null}
                title={gesperrtHinweis ?? texte.titel}
            >
                <TaskListLtr20Regular />
                {zugeordnet && (
                    <span className="tp-vg-haken" aria-hidden="true">
                        {I.check}
                    </span>
                )}
                <span className={variante === "zeile" ? "tp-rowact-label" : undefined}>
                    {anzahl > 1 ? `Vorgang (${anzahl})` : "Vorgang"}
                </span>
                <span className="tp-vgchev" aria-hidden="true">
                    {I.chevD}
                </span>
            </button>
            <RdContextMenu
                position={menu}
                items={items}
                onClose={schliessen}
                ankerRef={btnRef}
            />
        </>
    );
};

export default CallVorgangButton;
